import React, { useState } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ScenarioBar from "../../components/ScenarioBar";
import { ROUTES, SCENARIOS, QUIZZES } from "../../utils/constants";
import { getScenarioNumByPath, getTimer } from "../../utils/helpers";
import { updateTime } from "../../redux/actions";
import { postAnswer } from "../../api/answer";
import { modalConfirm, modalQuestionResolution, modalHint, modalHintShow } from "../../utils/logger";
import { IconRestore, IconHandMove } from "@tabler/icons-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import CheckButton from "../../components/CheckButton";
import estasseguro from "../../assets/bg/estasseguro-avatar-blue.png";
import respuestacorrecta from "../../assets/bg/correcto-avatar.png";
import respuestaincorrecta from "../../assets/bg/incorrecto-avatar.png";
import btnPistaBlue from "../../assets/btnPistaBlue.png";
import imageHint from "../../assets/hints/imageHintBlue.png";
import imageHintUsed from "../../assets/btnPistaBlue.png";
import unIntento from "../../assets/un-intento-blue.png";
import dosIntentos from "../../assets/dos-intentos-blue.png";
import imgPista from "../../assets/hints/hint-scenario4-test3.png";

function Scenario4Pregunta9({ intl }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const group = useSelector((state) => state.group);

  const activeScenario = getScenarioNumByPath(location.pathname);

  const QUIZ_ID = QUIZZES.PREGUNTA9;
  const SCENARIO_ID = SCENARIOS.SCENARIO_4;

  // ⚙️ Puntuación / intentos
  const NUMBERATTEMPTS = 2;
  const CORRECT_LABELS_SET = new Set([
    "Inmunoglobulinas (IVIG)",
    "Profilaxis antiviral y anti-PJP",
    "Actualizar el calendario de vacunación",
  ]);
  const CORRECT_ANSWER_POINTS = 10;
  const WRONG_ANSWER_POINTS = -5;
  const CORRECT_ANSWER_POINTS_2 = 3;
  const WRONG_ANSWER_POINTS_2 = 0;

  // Estado drag & drop: 7 opciones, 3 escudos (el orden no importa)
  const items = [
    { id: "profilaxis-antiviral", label: "Profilaxis antiviral y anti-PJP" },
    { id: "actualizar-calendario", label: "Actualizar el calendario de vacunación" },
    { id: "inmunoglobulinas", label: "Inmunoglobulinas (IVIG)" },
    { id: "aislamiento-baja", label: "Aislamiento estricto y baja laboral permanente" },
    { id: "tocilizumab", label: "Tocilizumab profiláctico mensual" },
    { id: "g-csf", label: "G-CSF profiláctico" },
    { id: "profilaxis-antibiotica", label: "Profilaxis antibiótica universal de amplio espectro" },
  ];
  const [slots, setSlots] = useState([null, null, null]);

  // Estado general
  const [isCompleted, setIsCompleted] = useState(false);
  const [hintUsed, setHintUsed] = useState(false);
  const [hintPenalty, setHintPenalty] = useState(0);
  const [hintAppliedToScore, setHintAppliedToScore] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 2 intentos con envío único (suma final)
  const [attempt, setAttempt] = useState(1);            // 1..2
  const [pendingScore, setPendingScore] = useState(0);  // acumulado a enviar al final

  async function submitAnswer(finalScore, correctFinal) {
    const totalTime = getTimer();
    dispatch(updateTime(totalTime, 1));

    try {
      const res = await postAnswer({
        scenario: SCENARIO_ID,
        quiz: QUIZ_ID,
        group: group?._id,
        score: finalScore, // único envío con la suma final
        time: totalTime,
        lastVisited: ROUTES.SCENARIO4_FIN,
        correct: correctFinal,
      });

      if (res.status === 201) {
        setIsCompleted(true);
        navigate(ROUTES.SCENARIO4_FIN);
      }
    } catch (error) {
      const status = error?.response?.status;
      const code = error?.response?.data?.code;

      if (status === 409 && code === "E_DUPLICATE_ANSWER") {
        await modalQuestionResolution({
          correct: false,
          question: "Respuesta ya registrada",
          textHtml: `<div class="txt32 txtBlue bold">Esta pregunta ya se había contestado con anterioridad y no se contabilizará, pueden continuar con el resto del juego.</div>`,
          customConfirmButtonHtml: "Ir a la conclusión",
        });
        navigate(ROUTES.SCENARIO4_FIN);
        return;
      }

      await modalQuestionResolution({
        correct: false,
        question: "No se pudo guardar",
        textHtml: `<div class="txt24 txt-red bold">Ha ocurrido un problema al registrar tu respuesta. Inténtalo de nuevo</div>`,
        customConfirmButtonHtml: "Aceptar",
      });

      setIsSubmitting(false);
    }
  }

  function resetValues() {
    setSlots([null, null, null]);
  }

  const useHint = async () => {
    // Tu contenido de pista: texto/HTML o URL de imagen
    const hint = `<img src=${imgPista} alt="Pista" style="width:100%;" />`;

    if (hintUsed) {
      // Si ya se usó, abre la pista directamente (sin confirmar)
      await modalHintShow({ hint, imageHintUsed, color: "blue", big: true });
      return;
    }

    // Primera vez: confirmar, mostrar pista y aplicar penalización
    const { used } = await modalHint({ image: imageHint, imageHintUsed, hint, color: "blue", big: true });
    if (used && !hintUsed) {
      setHintUsed(true);
      setHintPenalty(3); // restaremos 3 puntos al enviar la respuesta
    }
  };

  function checkAnswer() {
    if (slots.includes(null)) {
      modalQuestionResolution({
        correct: false,
        textHtml: `<img src=${estasseguro} alt="Debes completar los espacios" class="estasseguro" />
                   <div class="text-center txtTitulos mt-3 blue">Debes completar los tres escudos</div>`,
      });
      return;
    }

    const labelsInSlots = slots.map((id) => items.find((it) => it.id === id)?.label).filter(Boolean);
    const selectedSet = new Set(labelsInSlots);
    const isCorrect =
      selectedSet.size === 3 &&
      labelsInSlots.every((label) => CORRECT_LABELS_SET.has(label));

    modalConfirm({ estasseguro, color: "blue" }).then((res) => {
      if (!res.isConfirmed) return;

      setIsSubmitting(true);
      const hintThisAttempt = hintUsed && !hintAppliedToScore ? hintPenalty : 0;

      if (isCorrect) {
        // ✅ Correcto (1º o 2º intento)
        const base = attempt === 1 ? CORRECT_ANSWER_POINTS : CORRECT_ANSWER_POINTS_2;
        const attemptDelta = base - hintThisAttempt;
        const newScore = pendingScore + attemptDelta;
        if (hintThisAttempt) setHintAppliedToScore(true);

        modalQuestionResolution({
          correct: true,
          question: "Escudos protectores que Juan debe llevarse a casa",
          textHtml: `<img src=${respuestacorrecta} alt="Respuesta correcta" style='max-width:420px' />
                     <div class="txt32 bold pt-4">Ganaron ${attemptDelta} puntos en este intento${hintThisAttempt ? " (-3 pista)" : ""}</div>
                     ${attempt === 2 ? `<div class="txt20 mt-2">Correcto en el segundo intento</div>` : ""}
                     <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>`,
          customConfirmButtonHtml: "Continuar",
          color: "blue",
        }).then(() => {
          submitAnswer(newScore, true); // envío único final
        });

        return;
      }

      // ❌ Incorrecto
      if (attempt < NUMBERATTEMPTS) {
        // 1er fallo: restamos -5 (y -3 si pista aquí). NO bloqueamos ni revelamos qué está mal.
        const attemptDelta = WRONG_ANSWER_POINTS - hintThisAttempt; // -5 o -8 si pista aquí
        const newScore = pendingScore + attemptDelta;
        if (hintThisAttempt) setHintAppliedToScore(true);

        modalQuestionResolution({
          correct: false,
          question: "Escudos protectores que Juan debe llevarse a casa",
          textHtml: `<img src=${respuestaincorrecta} alt="Respuesta incorrecta" style='max-width:420px' />
                     <div class="txt28 bold pt-3">La combinación no es correcta. ${attemptDelta} puntos${hintThisAttempt ? " (incluye -3 por pista)" : ""}</div>
                     <div class="txt22 mt-2">Les queda <b>1</b> intento. Revisa tu respuesta y vuelve a intentarlo.</div>
                     <div class="txt20 mt-2">Acumulado hasta ahora: <b>${newScore}</b></div>`,
          customConfirmButtonHtml: "Intentar de nuevo",
          color: "blue",
        }).then(() => {
          setPendingScore(newScore);
          setAttempt(attempt + 1);
          // No bloqueamos ni reseteamos los slots (pueden ajustarlos manualmente).
          setIsSubmitting(false);
        });

        return;
      }

      // 2º fallo: delta 0 (o -3 si justo aquí aplica pista por 1ª vez). Enviamos.
      const attemptDelta = WRONG_ANSWER_POINTS_2 - hintThisAttempt; // 0 o -3
      const newScore = pendingScore + attemptDelta;
      if (hintThisAttempt) setHintAppliedToScore(true);

      modalQuestionResolution({
        correct: false,
        question: "Escudos protectores que Juan debe llevarse a casa",
        textHtml: `<img src=${respuestaincorrecta} alt="Respuesta incorrecta" style='max-width:420px' />
                   <div class="txt32 bold pt-4">Han agotado los intentos</div>
                   ${hintThisAttempt ? `<div class="txt20 mt-2">Se aplicó -3 por pista en este intento</div>` : ""}
                   <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>`,
        customConfirmButtonHtml: "Continuar",
        color: "blue",
      }).then(() => {
        submitAnswer(newScore, false); // envío único final
      });
    });
  }

  // 🔄 Drag & Drop (solo pool → escudo y escudo → escudo; quitar del escudo solo con Reset)
  const onDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;

    // No permitir soltar en el pool desde un escudo (evita comportamiento errático)
    if (source.droppableId.startsWith("slot") && destination.droppableId === "pool") {
      return;
    }

    const newSlots = [...slots];

    // Pool -> Slot
    if (source.droppableId === "pool" && destination.droppableId.startsWith("slot")) {
      const slotIndex = parseInt(destination.droppableId.replace("slot-", ""));
      newSlots[slotIndex] = draggableId;
    }

    // Slot -> Slot
    if (source.droppableId.startsWith("slot") && destination.droppableId.startsWith("slot")) {
      const fromIndex = parseInt(source.droppableId.replace("slot-", ""));
      const toIndex = parseInt(destination.droppableId.replace("slot-", ""));
      newSlots[toIndex] = draggableId;
      newSlots[fromIndex] = null;
    }

    setSlots(newSlots);
  };

  const remainingAttempts = NUMBERATTEMPTS - attempt + 1;

  return (
    <div className="h-100">
      <div className="container-fluid h-100 px-5 d-flex flex-column justify-content-start pt-2">
        <div className="notaCasoClinicoAutor">caso clínico del autor</div>
        <h3 className="txt24 txt-white text-center" style={{ backgroundColor: "#69D0FF", borderRadius: "10px", padding: "20px"}}>
          Teclistamab conlleva un riesgo de infecciones.<br/>
          Arrastren los 3 escudos protectores que Juan debe llevarse a casa.
        </h3>
        <DragDropContext onDragEnd={onDragEnd}>
          {/* 3 escudos con forma curvada (SVG) y contorno visible cuando vacío */}
          <div className="row mt-2">
            <div className="col-12 d-flex justify-content-center align-items-stretch gap-4 flex-wrap">
              {[0, 1, 2].map((slotIndex) => {
                const shieldPath = "M 5,0 L 95,0 L 95,30 C 95,62 76,84 50,105 C 24,84 5,62 5,30 Z";
                return (
                  <Droppable key={slotIndex} droppableId={`slot-${slotIndex}`}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        style={{
                          position: "relative",
                          width: "160px",
                          minWidth: "160px",
                          height: "200px",
                          minHeight: "200px",
                        }}
                      >
                        <svg
                          style={{ position: "absolute", left: 0, top: "0", width: "100%", height: "100%", pointerEvents: "none" }}
                          viewBox="0 0 100 110"
                          preserveAspectRatio="xMidYMid meet"
                          aria-hidden="true"
                        >
                          <defs>
                            {/* Clip interior para el texto: escudo reducido para que no tape letras en los laterales */}
                            <clipPath id={`shield-clip-p9-inner-${slotIndex}`} clipPathUnits="objectBoundingBox">
                              <path
                                d="M 0.05,0 L 0.95,0 Q 1,0.42 0.5,0.958 Q 0,0.42 0.05,0 Z"
                                transform="translate(0.5, 0.48) scale(0.78) translate(-0.5, -0.48)"
                              />
                            </clipPath>
                          </defs>
                          {/* Relleno azul cuando tiene opción */}
                          {slots[slotIndex] && (
                            <path d={shieldPath} fill="#69D0FF" />
                          )}
                          {/* Contorno siempre visible (vacío o lleno) */}
                          <path
                            d={shieldPath}
                            fill="none"
                            stroke="#69D0FF"
                            strokeWidth="2.5"
                          />
                        </svg>
                        {slots[slotIndex] ? (
                          <Draggable draggableId={slots[slotIndex]} index={0}>
                            {(providedDrag) => {
                              const item = items.find((it) => it.id === slots[slotIndex]);
                              return (
                                <div
                                  ref={providedDrag.innerRef}
                                  {...providedDrag.draggableProps}
                                  {...providedDrag.dragHandleProps}
                                  className="d-flex align-items-center justify-content-center text-center text-white"
                                  style={{
                                    position: "absolute",
                                    left: 0,
                                    top: "0",
                                    width: "100%",
                                    height: "100%",
                                    clipPath: `url(#shield-clip-p9-inner-${slotIndex})`,
                                    wordBreak: "break-word",
                                    lineHeight: "1.2",
                                    cursor: "grab",
                                    padding: "14px 10px",
                                    boxSizing: "border-box",
                                  }}
                                >
                                  <div
                                  className="txt16"
                                  style={{
                                    position:"absolute",
                                    top: "16px",
                                    width: "74%"
                                  }}>
                                  {item?.label}
                                  </div>
                                </div>
                              );
                            }}
                          </Draggable>
                        ) : null}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                );
              })}
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              {/* Pool de palabras (solo las que NO estén en slots) */}
              <Droppable droppableId="pool" direction="horizontal">
                {(provided) => (
                  <div
                    className="position-relative d-flex justify-content-between mt-1"
                    style={{ backgroundColor: "#69D0FF44", borderRadius: "10px", padding: "5px"}}
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    <div className="d-flex align-items-start flex-wrap">
                      {items
                        .filter((it) => !slots.includes(it.id))
                        .map((item, index) => (
                          <Draggable key={item.id} draggableId={item.id} index={index}>
                            {(provided) => (
                              <div
                                className="mx-2 px-3 py-1 my-1 rounded shadow-sm txt16 bold cursor-pointer dragWidth text-center"
                                style={{ backgroundColor: "#69D0FF", color: "white" }}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                {item.label}
                              </div>
                            )}
                          </Draggable>
                        ))}
                      {provided.placeholder}
                    </div>

                    <button
                      className="btn-main bold shadow bgBlue mt-1 me-1"
                      onClick={resetValues}
                      style={{ minHeight: "70px", minWidth: "70px" }}
                    >
                      <IconRestore size={30} color="white" stroke={2} />
                    </button>
                  </div>
                )}
              </Droppable>
            </div>
            <div className="col-12">
              <div className="d-flex align-items-center justify-content-between mt-2">
                <div className="d-flex justify-content-end mb-1">
                    <img src={remainingAttempts == 2 ? dosIntentos : unIntento} className="imgIntentos" />
                </div>
                <h5 className="mb-4 txt18 text-right my-2 blue">
                  <IconHandMove size={20} color="#69D0FF" stroke={1} /> Mantengan presionado la opción unos instantes para llevarla a uno de los escudos.<br/>
                  Si quieren cambiar la respuesta antes de validar, cliquen el botón Reset <IconRestore size={20} color="#69D0FF" stroke={2} />
                </h5>
              </div>
            </div>
          </div>
        </DragDropContext>

        <div className="d-flex justify-content-between mt-2">
          <div className="position-relative d-inline-block">
            <img
              src={btnPistaBlue}
              className={`btnPista mt-2 ${hintUsed ? "used" : ""}`}
              onClick={useHint}
              alt={hintUsed ? "Pista usada" : "Usar pista"}
              title={hintUsed ? "Pista usada (-3)" : "Usar pista (-3 puntos)"}
            />
            {hintUsed && <span className="badge-penalty">-3</span>}
          </div>
          <CheckButton
            text="Comprobar respuesta"
            onClick={checkAnswer}
            disabled={isCompleted || isSubmitting}
            color="blue"
            loading={isSubmitting}
          />
        </div>
      </div>

      <ScenarioBar activeScenario={activeScenario} />
    </div>
  );
}

export default injectIntl(Scenario4Pregunta9);
